/**
 * CanvasInfoPopover.tsx - Compact popover showing editor shortcuts and mouse interactions.
 *
 * Anchored below the info button in CanvasControls. Displays a curated subset
 * of keyboard shortcuts and mouse interactions so users can discover features
 * without opening the full KeyboardShortcutsHelp modal.
 *
 * Dismisses on click-outside or Escape (same pattern as CanvasContextMenu).
 */

import { css } from 'aphrodite';
import { useEffect, useRef } from 'react';
import { styleSheet as styles } from './styles/CanvasInfoPopoverStyles';

interface CanvasInfoPopoverProps {
	onClose: () => void;
	onShowAllShortcuts: () => void;
}

export function CanvasInfoPopover({ onClose, onShowAllShortcuts }: CanvasInfoPopoverProps) {
	const popoverRef = useRef<HTMLDivElement>(null);

	// Close on Escape key.
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [onClose]);

	// Close when clicking outside. Delayed to prevent the opening click from
	// immediately dismissing the popover (same pattern as CanvasContextMenu).
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
				onClose();
			}
		};

		const timeoutId = setTimeout(() => {
			document.addEventListener('mousedown', handleClickOutside);
		}, 0);

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [onClose]);

	const handleShowAll = () => {
		onClose();
		onShowAllShortcuts();
	};

	return (
		<div ref={popoverRef} className={css(styles.popover)} role='dialog' aria-label='Canvas shortcuts'>
			{/* Mouse interactions */}
			<div className={css(styles.section)}>
				<h4 className={css(styles.sectionTitle)}>Mouse</h4>
				<div className={css(styles.itemList)}>
					<ShortcutRow badge='Drag' label='Move from palette onto canvas' isMouse />
					<ShortcutRow badge='Click' label='Click + button to add a step' isMouse />
					<ShortcutRow badge='Right-click' label='Node context menu' isMouse />
					<ShortcutRow badge='Scroll' label='Pan the canvas' isMouse />
					<ShortcutRow keys={['Ctrl', 'Scroll']} label='Zoom in / out' />
					<ShortcutRow badge='Drag canvas' label='Selection box' isMouse />
					<ShortcutRow keys={['Shift']} label='Click to multi-select' badge='Click' isMouse />
				</div>
			</div>

			{/* Keyboard shortcuts (curated subset) */}
			<div className={css(styles.section)}>
				<h4 className={css(styles.sectionTitle)}>Keyboard</h4>
				<div className={css(styles.itemList)}>
					<ShortcutRow keys={['Ctrl', 'Z']} label='Undo' />
					<ShortcutRow keys={['Ctrl', 'Shift', 'Z']} label='Redo' />
					<ShortcutRow keys={['Ctrl', 'C']} label='Copy' />
					<ShortcutRow keys={['Ctrl', 'V']} label='Paste' />
					<ShortcutRow keys={['Ctrl', 'D']} label='Duplicate' />
					<ShortcutRow keys={['Ctrl', 'F']} label='Search' />
					<ShortcutRow keys={['Delete']} label='Delete selected' />
					<ShortcutRow keys={['F']} label='Fit view' />
					<ShortcutRow keys={['Z']} label='Zoom to selection' />
					<ShortcutRow keys={['Ctrl', 'E']} label='Expand/collapse all' />
				</div>
			</div>

			{/* Footer link to full modal */}
			<button type='button' className={css(styles.footerLink)} onClick={handleShowAll}>
				Press <kbd className={css(styles.kbd)}>?</kbd> for all keyboard shortcuts
			</button>
		</div>
	);
}

// -- Row sub-component ---------------------------------------------------

interface ShortcutRowProps {
	label: string;
	keys?: string[];
	badge?: string;
	isMouse?: boolean;
}

function ShortcutRow({ label, keys, badge, isMouse }: ShortcutRowProps) {
	return (
		<div className={css(styles.row)}>
			<div className={css(styles.keysCell)}>
				{keys?.map((key, i) => (
					<span key={i}>
						<kbd className={css(styles.kbd)}>{key}</kbd>
						{i < keys.length - 1 && <span className={css(styles.keySep)}>+</span>}
					</span>
				))}
				{badge && <span className={css(isMouse ? styles.mouseBadge : styles.kbd)}>{badge}</span>}
			</div>
			<span className={css(styles.label)}>{label}</span>
		</div>
	);
}

// -- InfoIcon (exported for CanvasControls) ------------------------------

export function InfoIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
			<circle cx='8' cy='8' r='6.5' stroke='currentColor' strokeWidth='1.5' fill='none' />
			<path d='M8 7v4' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
			<circle cx='8' cy='5' r='0.75' fill='currentColor' />
		</svg>
	);
}

// -- Styles --------------------------------------------------------------
